// ─────────────────────────────────────────────────────────
// Configurator — state and validation types
// ─────────────────────────────────────────────────────────

export type WindingType = "plain" | "round_wound" | "flat_wound" | "half_wound";

export type { StringConstruction } from "@/lib/configurator/preset-sets";
import type { StringConstruction } from "@/lib/configurator/preset-sets";

export interface ConfiguratorString {
  position: number;
  // Stored as a string (e.g. "0.009") to avoid float precision issues
  gaugeValue: string;
  windingType: WindingType;
  // null = inherits set-level color
  colorId: string | null;
  // null = inherits set-level scale length
  scaleLengthId: string | null;
}

export interface ConfiguratorState {
  // Step 1
  instrumentId: string | null;
  // Step 2
  stringCountId: string | null;
  stringCount: number | null;
  scaleLengthId: string | null;
  // Step 3
  lineId: string | null;
  stringTypeId: string | null;
  // Step 4
  presetId: string | null;
  /**
   * Display-only provenance for a configuration that started from a preset and
   * was then edited ("Modified from Regular Light"). Never sent to the API and
   * never a claim that the catalog set is unchanged — presetId is cleared the
   * moment a gauge or winding is edited.
   */
  basePresetName: string | null;
  strings: ConfiguratorString[];
  isCustomGauge: boolean;
  // Step 5
  colorId: string | null;
  isPerStringColor: boolean;
  /**
   * Per-string colours kept while per-string mode is switched off, so toggling
   * the mode is reversible instead of destructive.
   */
  stringColorDraft: { position: number; colorId: string | null }[] | null;
  // Step 6
  setName: string;
  quantity: number;
  // Computed
  isNonStandard: boolean;
  unitPrice: number | null;
  totalPrice: number | null;
}

export const CONFIGURATOR_INITIAL_STATE: ConfiguratorState = {
  instrumentId: null,
  stringCountId: null,
  stringCount: null,
  scaleLengthId: null,
  lineId: null,
  stringTypeId: null,
  presetId: null,
  basePresetName: null,
  strings: [],
  isCustomGauge: false,
  colorId: null,
  isPerStringColor: false,
  stringColorDraft: null,
  setName: "",
  quantity: 1,
  isNonStandard: false,
  unitPrice: null,
  totalPrice: null,
};

// ─── Reference data shapes (loaded from API) ─────────────

export interface InstrumentOption {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string | null;
  emoji: string | null;
  accentColor: string | null;
  shopSlug: string | null;
  shopLabel: string | null;
  shopBlurb: string | null;
  stringCounts: StringCountOption[];
  scaleLengths: ScaleLengthOption[];
}

export interface StringCountOption {
  id: string;
  count: number;
  label: string;
  isStandard: boolean;
}

export interface ScaleLengthOption {
  id: string;
  name: string;
  slug: string;
  inches: string;
  isDefault: boolean;
}

export interface StringLineOption {
  id: string;
  name: string;
  slug: string;
  productCount: number;
  description: string | null;
  baseMaterial: string | null;
  tagline: string | null;
  priceHint: string | null;
  features: string[];
  recommended: boolean;
  colors: ColorOption[];
  presets: GaugePresetOption[];
  stringTypes: StringTypeOption[];
}

export interface StringTypeOption {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  baseMaterial: string | null;
  tagline: string | null;
  features: string[];
  isDefault: boolean;
  // null = available on every instrument
  instrumentSlug: string | null;
  sortOrder: number;
}

export interface GaugePresetOption {
  id: string;
  instrumentSlug: string;
  name: string;
  displayLabel: string | null;
  /** False when no Aurora catalog product backs this exact set. */
  isCatalogSet: boolean;
  /**
   * String type whose own capability rows approve every string in this set.
   * null when the line offers no string-type choice. The same preset appears
   * once per approving type, each carrying that type's construction.
   */
  stringTypeSlug: string | null;
  /** Why an extrapolated set is offered; null for catalog sets. */
  note: string | null;
  strings: {
    position: number;
    gaugeValue: string;
    windingType: WindingType;
    construction: StringConstruction;
  }[];
}

export interface AllowedGaugeOption {
  gaugeValue: string;
  windingType: WindingType;
}

export interface ColorOption {
  id: string;
  name: string;
  slug: string;
  hexCode: string | null;
  swatchCss: string | null;
  /** Instrument this color is offered on for the parent line. */
  instrumentSlug: string;
}

export interface ConfiguratorReferenceData {
  instruments: InstrumentOption[];
  lines: StringLineOption[];
}

// ─── Pricing ──────────────────────────────────────────────

export interface Surcharge {
  key: string;
  name: string;
  amount: number;
}

export interface PriceBreakdown {
  basePrice: number;
  surcharges: Surcharge[];
  unitPrice: number;
  quantityMultiplier: number;
  discountPercent: number;
  totalPrice: number;
}

// ─── Validation ───────────────────────────────────────────

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}
